import { Shield, Award, MapPin, Phone, Mail, Clock, CheckCircle, Banknote, Bitcoin, Users, Globe, Building2, Target, Eye, Heart, TrendingUp, DollarSign, Coins } from "lucide-react";
import { Link } from "react-router-dom";

// Currency and crypto images
const CURRENCY_IMAGES = {
  usd: "https://images.unsplash.com/photo-1621761191319-c6fb62004040?w=600",
  crypto: "https://images.unsplash.com/photo-1518546305927-5a555bb7020d?w=600",
  cad: "https://images.unsplash.com/photo-1621416894569-0f39ed31d247?w=600",
  btc: "https://images.unsplash.com/photo-1516245834210-c4c142787335?w=600",
  exchange: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=600",
  team: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=600"
};

const stats = [
  { value: "500+", label: "Happy Customers", icon: Users },
  { value: "25+", label: "Fiat Currencies", icon: Globe },
  { value: "25+", label: "Cryptocurrencies", icon: Coins },
  { value: "100%", label: "FINTRAC Compliant", icon: Shield },
];

const values = [
  {
    icon: Shield,
    title: "Trust & Security",
    description: "Your security is our top priority. We implement bank-level security measures and are fully compliant with FINTRAC regulations."
  },
  {
    icon: Target,
    title: "Transparency",
    description: "No hidden fees, no surprises. We display our rates clearly and ensure you know exactly what you'll receive before any transaction."
  },
  {
    icon: Heart,
    title: "Customer First",
    description: "Every decision we make is centered around providing the best experience for our customers. Your satisfaction is our success."
  },
  {
    icon: TrendingUp,
    title: "Competitive Rates",
    description: "We work hard to offer some of the most competitive exchange rates in the Greater Vancouver area for both fiat and cryptocurrency."
  }
];

const services = [
  {
    icon: Banknote,
    title: "Fiat Currency Exchange",
    description: "Exchange major world currencies including USD, EUR, GBP, INR, PHP, and 20+ more at competitive rates.",
    currencies: ["USD", "EUR", "GBP", "INR", "PHP", "CNY", "JPY", "AUD"]
  },
  {
    icon: Bitcoin,
    title: "Cryptocurrency Exchange",
    description: "Buy and sell popular cryptocurrencies including Bitcoin, Ethereum, USDT, and more with instant settlement.",
    currencies: ["BTC", "ETH", "USDT", "XRP", "SOL", "ADA", "DOGE", "BNB"]
  },
  {
    icon: Building2,
    title: "Business Services",
    description: "Tailored currency solutions for businesses including bulk exchanges, scheduled transactions, and corporate accounts.",
    features: ["Bulk Exchange", "Corporate Accounts", "Wire Transfers", "Business Rates"]
  },
  {
    icon: Users,
    title: "Personal Services",
    description: "Whether you're traveling, sending money to family, or investing in crypto, we provide personalized service for all your needs.",
    features: ["Travel Money", "Remittances", "Investment", "Bill Payments"]
  }
];

const team = [
  { role: "Expert Staff", desc: "Our team has combined experience of 20+ years in currency exchange and financial services." },
  { role: "Multilingual Support", desc: "We serve customers in English, Punjabi, Hindi, and Mandarin to better assist our diverse community." },
  { role: "Licensed Professionals", desc: "All our staff are trained and certified in anti-money laundering (AML) procedures." }
];

export default function About() {
  return (
    <div className="min-h-screen" data-testid="about-page">
      {/* Hero Section */}
      <section className="hero-gradient py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="trust-badge mb-6">
                <Shield className="w-4 h-4" />
                <span>FINTRAC Registered MSB</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-6">
                About <span className="text-[#3B82F6]">FonderX</span>
              </h1>
              <p className="text-xl text-blue-100/70 leading-relaxed mb-8">
                Your trusted currency exchange partner in New Westminster, BC. We provide reliable, 
                secure, and competitive exchange services for both fiat currencies and cryptocurrency.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/contact" className="btn-primary inline-flex items-center gap-2">
                  Visit Our Office
                  <MapPin className="w-4 h-4" />
                </Link>
                <a href="tel:+17787590400" className="btn-secondary inline-flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Call Us
                </a>
              </div>
            </div>
            
            {/* Hero Image Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="rounded-2xl overflow-hidden h-40 sm:h-48">
                  <img src={CURRENCY_IMAGES.usd} alt="US Dollars" className="w-full h-full object-cover" />
                </div>
                <div className="rounded-2xl overflow-hidden h-32 sm:h-40">
                  <img src={CURRENCY_IMAGES.btc} alt="Bitcoin" className="w-full h-full object-cover" />
                </div>
              </div>
              <div className="space-y-4 pt-8">
                <div className="rounded-2xl overflow-hidden h-32 sm:h-40">
                  <img src={CURRENCY_IMAGES.crypto} alt="Cryptocurrency" className="w-full h-full object-cover" />
                </div>
                <div className="rounded-2xl overflow-hidden h-40 sm:h-48">
                  <img src={CURRENCY_IMAGES.exchange} alt="Exchange Trading" className="w-full h-full object-cover" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-[#001a40] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="w-12 h-12 rounded-xl bg-[#3B82F6]/20 flex items-center justify-center mx-auto mb-3">
                  <stat.icon className="w-6 h-6 text-[#3B82F6]" />
                </div>
                <p className="text-2xl md:text-3xl font-bold text-white mb-1">{stat.value}</p>
                <p className="text-blue-100/50 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="bg-[#002055] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              {/* Mission */}
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-[#3B82F6]/20 flex items-center justify-center">
                    <Target className="w-5 h-5 text-[#3B82F6]" />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-white">Our Mission</h2>
                </div>
                <p className="text-blue-200/80 leading-relaxed text-lg">
                  To provide our community with accessible, transparent, and competitive currency exchange 
                  services. We believe everyone deserves fair rates and exceptional service when exchanging 
                  their hard-earned money.
                </p>
              </div>
              
              {/* Vision */}
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                    <Eye className="w-5 h-5 text-emerald-400" />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-white">Our Vision</h2>
                </div>
                <p className="text-blue-200/80 leading-relaxed text-lg">
                  To become the most trusted currency exchange in British Columbia, known for our 
                  integrity, competitive rates, and commitment to customer satisfaction. We aim to 
                  bridge traditional finance with the world of cryptocurrency.
                </p>
              </div>

              <div className="space-y-3 pt-4">
                {["FINTRAC Registered & Fully Compliant", "Competitive Exchange Rates Updated Daily", "Fast & Secure Transactions", "Professional Multilingual Staff", "Both Fiat & Cryptocurrency Services"].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                    <span className="text-white">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Image */}
            <div className="relative">
              <div className="rounded-2xl overflow-hidden">
                <img src={CURRENCY_IMAGES.team} alt="FonderX Office" className="w-full h-[400px] object-cover" />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-[#0a1628] border border-white/10 rounded-xl p-6 max-w-xs">
                <Award className="w-10 h-10 text-[#3B82F6] mb-3" />
                <h3 className="text-white font-semibold mb-2">Trusted Since Day One</h3>
                <p className="text-blue-100/60 text-sm">Serving New Westminster and the Greater Vancouver area with integrity.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="bg-[#001435] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Our Core Values</h2>
            <p className="text-blue-200/70 max-w-2xl mx-auto">
              These principles guide everything we do at FonderX
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, idx) => (
              <div key={idx} className="bg-white/5 backdrop-blur rounded-xl p-6 border border-white/10 hover:border-[#3B82F6]/30 transition-colors">
                <div className="w-12 h-12 rounded-xl bg-[#3B82F6]/20 flex items-center justify-center mb-4">
                  <value.icon className="w-6 h-6 text-[#3B82F6]" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{value.title}</h3>
                <p className="text-blue-100/60 text-sm leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="bg-[#002055] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Our Services</h2>
            <p className="text-blue-200/70 max-w-2xl mx-auto">
              Comprehensive currency exchange solutions for individuals and businesses
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((service, idx) => (
              <div key={idx} className="bg-white/5 backdrop-blur rounded-xl p-6 sm:p-8 border border-white/10">
                <div className="flex items-start gap-4 mb-4">
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    idx === 0 ? 'bg-green-500/20' : idx === 1 ? 'bg-amber-500/20' : idx === 2 ? 'bg-purple-500/20' : 'bg-blue-500/20'
                  }`}>
                    <service.icon className={`w-7 h-7 ${
                      idx === 0 ? 'text-green-400' : idx === 1 ? 'text-amber-400' : idx === 2 ? 'text-purple-400' : 'text-blue-400'
                    }`} />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">{service.title}</h3>
                    <p className="text-blue-100/60 text-sm leading-relaxed">{service.description}</p>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-white/10">
                  {(service.currencies || service.features)?.map((item, i) => (
                    <span key={i} className="px-3 py-1 bg-white/5 rounded-full text-xs text-blue-100/70">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="bg-[#002055] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Our Team</h2>
            <p className="text-blue-200/70 max-w-2xl mx-auto">
              Dedicated professionals committed to serving you
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {team.map((member, idx) => (
              <div key={idx} className="bg-white/5 backdrop-blur rounded-xl p-6 border border-white/10 text-center">
                <div className="w-16 h-16 rounded-full bg-[#3B82F6]/20 flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-[#3B82F6]" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{member.role}</h3>
                <p className="text-blue-100/60 text-sm">{member.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Location Section with Map */}
      <section className="section-light py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Visit Our Office</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Conveniently located in New Westminster, BC - serving the Greater Vancouver area
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Map */}
            <div className="rounded-2xl overflow-hidden h-[400px] shadow-lg">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2607.5!2d-122.912!3d49.206!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x5485d8b0a1b8c5e5%3A0x0!2s723%2012th%20St%2C%20New%20Westminster%2C%20BC!5e0!3m2!1sen!2sca!4v1"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                title="FonderX Location"
                className="grayscale-[20%]"
              ></iframe>
            </div>

            {/* Contact Details */}
            <div className="space-y-6">
              <div className="flex items-start gap-4 p-5 bg-white rounded-xl border border-slate-200 shadow-sm">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h4 className="text-slate-900 font-semibold mb-1">Address</h4>
                  <p className="text-slate-600">2-723 12th Street</p>
                  <p className="text-slate-600">New Westminster, BC V3M 4J8</p>
                  <a href="https://maps.google.com/?q=2-723+12th+Street+New+Westminster+BC" target="_blank" rel="noopener noreferrer" className="text-blue-600 text-sm hover:underline mt-2 inline-block">
                    Get Directions →
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4 p-5 bg-white rounded-xl border border-slate-200 shadow-sm">
                <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h4 className="text-slate-900 font-semibold mb-1">Phone</h4>
                  <a href="tel:+17787590400" className="text-slate-600 hover:text-blue-600 text-lg">+1 (778) 759-0400</a>
                </div>
              </div>

              <div className="flex items-start gap-4 p-5 bg-white rounded-xl border border-slate-200 shadow-sm">
                <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h4 className="text-slate-900 font-semibold mb-1">Email</h4>
                  <a href="mailto:info@fonderx.com" className="text-slate-600 hover:text-blue-600">info@fonderx.com</a>
                </div>
              </div>

              <div className="flex items-start gap-4 p-5 bg-white rounded-xl border border-slate-200 shadow-sm">
                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h4 className="text-slate-900 font-semibold mb-1">Business Hours</h4>
                  <p className="text-slate-600">Monday - Friday: 9:30 AM - 6:00 PM</p>
                  <p className="text-slate-600">Saturday - Sunday: Closed</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-[#001435] to-[#003080] py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Exchange?
          </h2>
          <p className="text-blue-200/70 text-lg mb-8">
            Visit our New Westminster office or give us a call to get started.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-primary inline-flex items-center justify-center gap-2">
              Contact Us
              <Mail className="w-4 h-4" />
            </Link>
            <a href="tel:+17787590400" className="btn-secondary inline-flex items-center justify-center gap-2">
              <Phone className="w-4 h-4" />
              +1 (778) 759-0400
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
